import { format, formatDistanceToNow, parseISO } from "date-fns";

// ---------------------------------------------------------------------------
// Generic utilities — safe to use across the app
// ---------------------------------------------------------------------------

export const paginate = (array: unknown[], pageSize: number, pageNumber: number): unknown[] => {
  return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
};

export const createMarkup = (textString: string): { __html: string } => {
  return { __html: textString };
};

export const stripHtml = (html: string): string => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent ?? div.innerText ?? "";
};

/**
 * Copy text to the clipboard. Uses the async Clipboard API when available and
 * falls back to a hidden textarea + execCommand for older/insecure contexts.
 * Resolves to true on success, false on failure.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path below
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};

export const removeEmptyObjectKey = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== ""),
  ) as Partial<T>;
};

export const getValueFromArrayOfObject = (
  arr: Array<{ key: string; value: unknown }>,
  key: string,
): unknown | null => {
  return arr.find((item) => item.key === key)?.value ?? null;
};

export const generateLink = (link: string): string => {
  return `${window.location.origin}${link}`;
};

export const getRandomName = (prefix = "", suffix = ""): string => {
  const random = `${Date.now()}_${Math.ceil(Math.random() * 1000)}`;
  return `${prefix}${random}${suffix}`;
};

// ---------------------------------------------------------------------------
// Date helpers (date-fns)
// ---------------------------------------------------------------------------

export const formatDate = (dateString: string, pattern = "MMM d, yyyy"): string => {
  try {
    return format(parseISO(dateString), pattern);
  } catch {
    return dateString;
  }
};

export const timeAgo = (dateString: string): string => {
  try {
    return formatDistanceToNow(parseISO(dateString), { addSuffix: true });
  } catch {
    return dateString;
  }
};

// ---------------------------------------------------------------------------
// API error extraction — works with LeadNova { success, data, error, meta } envelope
// ---------------------------------------------------------------------------

export const extractErrorMessage = (e: unknown, defaultMessage = "Please try again"): string => {
  if (typeof e === "string") return e;

  const err = e as Record<string, unknown>;

  // LeadNova envelope: { error: { message } }
  if (err?.error && typeof err.error === "object") {
    const apiError = err.error as Record<string, unknown>;
    if (typeof apiError.message === "string") return apiError.message;
  }

  // Axios response shape
  if (err?.data && typeof err.data === "object") {
    const data = err.data as Record<string, unknown>;
    if (typeof data.message === "string") return data.message;

    // Validation errors: { errors: { field: [messages] } }
    if (data.errors && typeof data.errors === "object") {
      const firstField = Object.values(data.errors as Record<string, string[]>)[0];
      if (Array.isArray(firstField) && firstField.length > 0) return firstField[0];
    }
  }

  return defaultMessage;
};
